import Input from "../../../../components/Input/Input";
import MuiSwitch from "../../../../components/MuiSwitch/MuiSwitch";
import { Company } from "../../../../types/Company";
import { formatDateForProjectInput } from "../projectFormModel";
import { ProjectFormController } from "../useProjectFormController";
import s from "../ProjectFormPage.module.css";

type AvailabilityStepProps = {
  company: Company;
  controller: ProjectFormController;
};

const AvailabilityStep = ({ company, controller }: AvailabilityStepProps) => {
  const setBooleanField = (
    field:
      | "skipSpecialistConfirmation"
      | "isPrivate"
      | "isPublicAccess"
      | "enableTwilioSms"
      | "allowSpecialistLeave"
      | "allowSpecialistPause"
      | "offerPromoPackages"
      | "isBarter"
      | "skipSpecialistRecordConfirmation",
    value: boolean,
  ) => {
    controller.setProjectData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className={s.stepPanel}>
      <div className={s.section}>
        <h2 className={s.sectionTitle}>Availability</h2>
        <p className={s.sectionText}>
          Configure access, limits, dates, messaging, and specialist
          participation behavior.
        </p>
      </div>

      <div className={s.fieldGrid}>
        <div className={s.fieldBlock}>
          <label className={s.fieldLabel}>Client limit</label>
          <Input
            inputValue={controller.projectData.clientLimit.toString()}
            onChangeInput={(value) =>
              controller.setProjectData((prev) => ({
                ...prev,
                clientLimit: Number(value),
              }))
            }
            isVisible
            required
            ariaLabel="Client limit"
          />
        </div>

        <div className={s.fieldBlock}>
          <label className={s.fieldLabel}>Specialist limit</label>
          <Input
            inputValue={controller.projectData.specialistLimit.toString()}
            onChangeInput={(value) => {
              const numericValue = value === "" ? 0 : Number(value);
              if (Number.isInteger(numericValue) && numericValue >= 0) {
                controller.setProjectData((prev) => ({
                  ...prev,
                  specialistLimit: numericValue,
                }));
              }
            }}
            isVisible
            required
            ariaLabel="Specialist limit"
          />
        </div>

        <div className={`${s.fieldBlock} ${s.fullWidthField}`}>
          <span className={s.fieldLabel}>Project end date</span>
          <input
            className={s.dateInput}
            type="date"
            value={
              controller.projectData.endDate
                ? formatDateForProjectInput(controller.projectData.endDate)
                : ""
            }
            onChange={(event) =>
              controller.setProjectData((prev) => ({
                ...prev,
                endDate: new Date(event.target.value),
              }))
            }
          />
        </div>
      </div>

      <div className={s.switchList}>
        <div className={s.switchElement}>
          <span>Booking without confirmation by a specialist</span>
          <MuiSwitch
            lablePlacement="start"
            checked={controller.projectData.skipSpecialistConfirmation ?? false}
            setChecked={(value) =>
              setBooleanField("skipSpecialistConfirmation", value)
            }
          />
        </div>
        <div className={s.switchElement}>
          <span>Private Project</span>
          <MuiSwitch
            lablePlacement="start"
            checked={controller.projectData.isPrivate}
            setChecked={(value) => setBooleanField("isPrivate", value)}
          />
        </div>
        <div className={s.switchElement}>
          <span>Public free Project</span>
          <MuiSwitch
            lablePlacement="start"
            checked={controller.projectData.isPublicAccess ?? false}
            setChecked={(value) => setBooleanField("isPublicAccess", value)}
          />
        </div>
        <div className={s.switchElement}>
          <span>Enable Twilio SMS</span>
          <MuiSwitch
            lablePlacement="start"
            checked={controller.projectData.enableTwilioSms}
            setChecked={(value) => setBooleanField("enableTwilioSms", value)}
          />
        </div>
        <div className={s.switchElement}>
          <span>Allow a specialist to leave a Project</span>
          <MuiSwitch
            lablePlacement="start"
            checked={controller.projectData.allowSpecialistLeave}
            setChecked={(value) =>
              setBooleanField("allowSpecialistLeave", value)
            }
          />
        </div>
        <div className={s.switchElement}>
          <span>Allow a specialist to pause a Project</span>
          <MuiSwitch
            lablePlacement="start"
            checked={controller.projectData.allowSpecialistPause}
            setChecked={(value) =>
              setBooleanField("allowSpecialistPause", value)
            }
          />
        </div>
        <div className={s.switchElement}>
          <span>Offer promo session packages</span>
          <MuiSwitch
            lablePlacement="start"
            checked={controller.projectData.offerPromoPackages}
            setChecked={(value) => setBooleanField("offerPromoPackages", value)}
          />
        </div>
        {company.isBarterAvailable && (
          <div className={s.switchElement}>
            <span>Enable barter sessions</span>
            <MuiSwitch
              lablePlacement="start"
              checked={controller.projectData.isBarter ?? false}
              setChecked={(value) => setBooleanField("isBarter", value)}
            />
          </div>
        )}
        <div className={s.switchElement}>
          <span>Skip specialist record confirmation</span>
          <MuiSwitch
            lablePlacement="start"
            checked={
              controller.projectData.skipSpecialistRecordConfirmation ?? false
            }
            setChecked={(value) =>
              setBooleanField("skipSpecialistRecordConfirmation", value)
            }
          />
        </div>
      </div>
    </div>
  );
};

export default AvailabilityStep;
